import {
    AuthRequiredError,
    GenerationApiError,
    PayloadTooLargeError
} from "./generation-api.js";

const DEFAULT_BASE_URL = readBaseUrl();

function readBaseUrl() {
    const value = import.meta.env?.VITE_API_BASE_URL;
    if (typeof value !== "string") return "";
    return value.replace(/\/+$/, "");
}

export function buildAccountSettingsJsonFromProject(deps = {}) {
    const {
        project = null,
        chat = {},
        ensureProjectPlcProfiles = () => [],
        normalizePlcProfile = (entry) => entry,
        ensureSignalCategories = () => [],
        ensureActuatorTypeCatalog = () => []
    } = deps;
    const sourceProject = project && typeof project === "object" ? project : {};
    const units = sourceProject.units || {};
    return {
        units: {
            linear: {
                distance: units.distanceLinear,
                accel: units.accelLinear,
                speed: units.speedLinear
            },
            rotary: {
                angle: units.angleRotary || "deg",
                accel: units.accelRotary || "deg/s2",
                speed: units.speedRotary || "deg/s"
            }
        },
        assistantDefaults: {
            plcProfileId: String(chat?.plcProfileId || sourceProject.selectedPlcProfileId || "generic-st").trim() || "generic-st"
        },
        controlSpecDefaults: {
            plcProfiles: ensureProjectPlcProfiles(sourceProject).map((entry, index) => normalizePlcProfile(entry, index)),
            signalCategories: ensureSignalCategories(sourceProject).map((entry) => ({
                id: entry.id,
                name: entry.name,
                direction: entry.direction,
                addressPrefix: entry.addressPrefix,
                startByte: entry.startByte
            })),
            actuatorTypeCatalog: ensureActuatorTypeCatalog(sourceProject).map((entry) => ({
                id: entry.id,
                label: entry.label,
                defaultActionNames: Array.isArray(entry.defaultActionNames) ? entry.defaultActionNames.slice() : []
            }))
        }
    };
}

export function applyAccountSettingsDefaultsToProject(deps = {}) {
    const {
        project = null,
        settingsJson = null,
        normalizePlcProfile = (entry) => entry,
        normalizeSignalCategoryEntry = (entry) => entry,
        normalizeActuatorTypeCatalogEntry = (entry) => entry,
        setCurrentProjectPlcProfileId = () => {},
        syncChatDialectFromProfile = () => {}
    } = deps;
    if (!project || typeof project !== "object" || !settingsJson || typeof settingsJson !== "object") return false;

    const projectUnits = project.units || (project.units = {});
    const accUnits = settingsJson.units;
    if (accUnits && typeof accUnits === "object") {
        if (accUnits.linear) {
            if (accUnits.linear.distance) projectUnits.distanceLinear = accUnits.linear.distance;
            if (accUnits.linear.accel) projectUnits.accelLinear = accUnits.linear.accel;
            if (accUnits.linear.speed) projectUnits.speedLinear = accUnits.linear.speed;
        }
        if (accUnits.rotary) {
            if (accUnits.rotary.angle) projectUnits.angleRotary = accUnits.rotary.angle;
            if (accUnits.rotary.accel) projectUnits.accelRotary = accUnits.rotary.accel;
            if (accUnits.rotary.speed) projectUnits.speedRotary = accUnits.rotary.speed;
        }
    }

    const assistantDefaults = settingsJson.assistantDefaults;
    if (assistantDefaults && typeof assistantDefaults === "object") {
        const plcProfileId = String(assistantDefaults.plcProfileId || "").trim();
        if (plcProfileId) {
            project.selectedPlcProfileId = plcProfileId;
            setCurrentProjectPlcProfileId(plcProfileId);
            syncChatDialectFromProfile();
        }
    }

    const defaults = settingsJson.controlSpecDefaults;
    if (defaults && typeof defaults === "object") {
        if (Array.isArray(defaults.plcProfiles) && defaults.plcProfiles.length) {
            project.plcProfiles = defaults.plcProfiles.map((entry, index) => normalizePlcProfile(entry, index));
        }
        if (Array.isArray(defaults.signalCategories) && defaults.signalCategories.length) {
            project.signalCategories = defaults.signalCategories.map((entry, index) => normalizeSignalCategoryEntry(entry, index));
        }
        if (Array.isArray(defaults.actuatorTypeCatalog) && defaults.actuatorTypeCatalog.length) {
            project.actuatorTypeCatalog = defaults.actuatorTypeCatalog.map((entry, index) => normalizeActuatorTypeCatalogEntry(entry, index));
        }
    }
    return true;
}

export async function saveAccountSettingsToCloud(deps = {}) {
    const {
        apiProjectsClient = null,
        refs = {},
        buildSettingsJson = () => null,
        updateAccountSettingsImpl = updateAccountSettings,
        setAccountSettings = () => {},
        schemaVersion = 1,
        setTimeoutFn = () => {},
        error = () => {}
    } = deps;
    if (!apiProjectsClient || typeof apiProjectsClient.getAccessToken !== "function") return false;

    const btnSaveAcc = refs.btnSaveAccountSettings;
    const statusEl = refs.accountSettingsStatus;
    if (btnSaveAcc) btnSaveAcc.disabled = true;
    if (statusEl) statusEl.textContent = "\u4fdd\u5b58\u4e2d...";

    try {
        const accessToken = await apiProjectsClient.getAccessToken();
        if (!accessToken) throw new Error("\u8a8d\u8a3c\u30c8\u30fc\u30af\u30f3\u306e\u53d6\u5f97\u306b\u5931\u6557\u3057\u307e\u3057\u305f");

        const settingsJson = buildSettingsJson();
        const res = await updateAccountSettingsImpl({ accessToken, settingsJson, schemaVersion });
        setAccountSettings(res);

        if (statusEl) {
            statusEl.textContent = "\u4fdd\u5b58\u5b8c\u4e86";
            statusEl.style.color = "#2a7a2a";
            setTimeoutFn(() => {
                if (statusEl.textContent === "\u4fdd\u5b58\u5b8c\u4e86") {
                    statusEl.textContent = "";
                }
            }, 3000);
        }
        return true;
    } catch (caughtError) {
        if (statusEl) {
            statusEl.textContent = "\u4fdd\u5b58\u5931\u6557: " + (caughtError?.message || caughtError);
            statusEl.style.color = "#a00";
        }
        error(caughtError);
        return false;
    } finally {
        if (btnSaveAcc) btnSaveAcc.disabled = false;
    }
}

function buildHeaders(accessToken, hasBody) {
    const headers = {};
    if (hasBody) headers["content-type"] = "application/json";
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    return headers;
}

function pickError(status, body) {
    const code = typeof body?.error === "string" ? body.error : null;
    const message =
        typeof body?.message === "string" && body.message
            ? body.message
            : code || `HTTP ${status}`;

    if (status === 401) return new AuthRequiredError(message, { status, code, details: body });
    if (status === 413) return new PayloadTooLargeError(message, { status, code, details: body });
    return new GenerationApiError(message, { status, code, details: body });
}

async function jsonFetch(url, init) {
    const response = await fetch(url, init);
    let parsed = null;
    try {
        parsed = await response.json();
    } catch {
        parsed = null;
    }
    if (!response.ok) {
        throw pickError(response.status, parsed);
    }
    return parsed;
}

function rootUrl(baseUrl) {
    return typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
}

export async function getAccountSettings({ accessToken, baseUrl, signal } = {}) {
    const url = `${rootUrl(baseUrl)}/api/account/settings`;
    return jsonFetch(url, {
        method: "GET",
        headers: buildHeaders(accessToken, false),
        signal
    });
}

export async function updateAccountSettings({ accessToken, settingsJson, schemaVersion = 1, baseUrl, signal } = {}) {
    const url = `${rootUrl(baseUrl)}/api/account/settings`;
    return jsonFetch(url, {
        method: "PUT",
        headers: buildHeaders(accessToken, true),
        body: JSON.stringify({ settings_json: settingsJson, schema_version: schemaVersion }),
        signal
    });
}

export async function loadAccountSettingsFromCloud(deps = {}) {
    const {
        apiProjectsClient = null,
        refs = {},
        getAccountSettingsImpl = getAccountSettings,
        setAccountSettings = () => {},
        warn = () => {}
    } = deps;
    if (!apiProjectsClient || typeof apiProjectsClient.getAccessToken !== "function") return false;

    try {
        const accessToken = await apiProjectsClient.getAccessToken();
        if (!accessToken) return false;
        const res = await getAccountSettingsImpl({ accessToken });
        setAccountSettings(res);

        const btnSaveAcc = refs.btnSaveAccountSettings;
        if (btnSaveAcc) {
            btnSaveAcc.disabled = false;
            btnSaveAcc.title = "現在の設定をアカウントのデフォルトとして保存";
        }
        return true;
    } catch (error) {
        warn("アカウント設定の取得に失敗しました", error);
        return false;
    }
}
